/**
 * Modèle de données des traces.
 *
 * Portage de `GpxTrack.kt`. Les identifiants de type de marqueur
 * (`GPM`, `Sprint`, …) sont des **données persistées**, pas des libellés :
 * ils sont traduits à l'affichage et ne doivent jamais être renommés.
 */

export interface TrackPoint {
  readonly lat: number
  readonly lon: number
  /** Altitude en mètres, `null` si absente du fichier. */
  readonly ele: number | null
  /** Horodatage conservé tel quel, sans reformatage. */
  readonly time: string | null
  readonly name: string | null
}

export interface Waypoint {
  readonly lat: number
  readonly lon: number
  readonly ele: number | null
  readonly name: string | null
  readonly desc: string | null
}

export interface TrackSegment {
  readonly points: readonly TrackPoint[]
}

/** Origine géométrique de la trace, affichée dans la fiche « Infos trace ». */
export type GpxSourceType = 'TRK' | 'WPT' | 'RTE'

/** Profil de routage OSRM. */
export type RoutingProfileId = 'CAR' | 'BIKE' | 'FOOT'

export interface RoutingProfile {
  readonly id: RoutingProfileId
  /** Sous-domaine de l'instance OSRM publique. */
  readonly serverPath: string
  /** Nom de profil dans le chemin de l'API. */
  readonly profileName: string
}

export const ROUTING_PROFILES: Record<RoutingProfileId, RoutingProfile> = {
  CAR: { id: 'CAR', serverPath: 'routed-car', profileName: 'driving' },
  BIKE: { id: 'BIKE', serverPath: 'routed-bike', profileName: 'bike' },
  FOOT: { id: 'FOOT', serverPath: 'routed-foot', profileName: 'foot' },
}

export function routingProfileFromName(name: string | null | undefined): RoutingProfile {
  if (name && name in ROUTING_PROFILES) {
    return ROUTING_PROFILES[name as RoutingProfileId]
  }
  return ROUTING_PROFILES.CAR
}

/* -------------------------------------------------------------------------
 * Marqueurs personnalisés
 * ---------------------------------------------------------------------- */

export const MARKER_TYPE_RADAR = 'Radar'
export const MARKER_TYPE_WAYOUT = 'WayOut'
export const MARKER_TYPE_DERIVATION = 'Derivation'
export const MARKER_TYPE_OTHER = 'Autre'

/** Le côté de la dérivation est porté par le symbole lui-même. */
export const DERIVATION_SYMBOL_LEFT = '↰'
export const DERIVATION_SYMBOL_RIGHT = '↱'

export interface PredefinedMarkerType {
  /** Identifiant persisté — ne jamais traduire ni renommer. */
  readonly id: string
  /** Clé de traduction du libellé affiché. */
  readonly labelKey: string
  readonly defaultSymbol: string
}

export const PREDEFINED_MARKER_TYPES: readonly PredefinedMarkerType[] = [
  { id: 'GPM', labelKey: 'marker_type_gpm', defaultSymbol: 'G' },
  { id: 'Sprint', labelKey: 'marker_type_sprint', defaultSymbol: 'S' },
  { id: 'Ravitaillement', labelKey: 'marker_type_feed', defaultSymbol: 'R' },
  { id: 'Dechets', labelKey: 'marker_type_waste', defaultSymbol: 'D' },
  { id: 'DrapeauJaune', labelKey: 'marker_type_yellowflag', defaultSymbol: 'DJ' },
  { id: MARKER_TYPE_RADAR, labelKey: 'marker_type_radar', defaultSymbol: 'R!' },
  { id: MARKER_TYPE_WAYOUT, labelKey: 'marker_type_wayout', defaultSymbol: 'WO' },
  {
    id: MARKER_TYPE_DERIVATION,
    labelKey: 'marker_type_derivation',
    defaultSymbol: DERIVATION_SYMBOL_LEFT,
  },
  { id: MARKER_TYPE_OTHER, labelKey: 'marker_type_other', defaultSymbol: '!' },
] as const

/**
 * Ramène un type inconnu sur « Autre ».
 *
 * L'APK a longtemps laissé créer des **modèles de marqueurs** : le marqueur né
 * d'un modèle portait l'identifiant de ce modèle dans son `type`, une chaîne
 * que rien ici ne sait interpréter. Ces modèles ont été retirés d'Android
 * (`CustomMarker.normalizeType`, v2.14.0) — le portage, lui, n'en a jamais eu
 * —, mais leurs marqueurs survivent dans les GPX déjà exportés, et rien
 * n'empêche un fichier tiers de porter un type inventé.
 *
 * Seul le type est perdu : nom, symbole, description et point kilométrique
 * sont portés par le marqueur lui-même, et « Autre » est précisément le type
 * dont le nom et le symbole se saisissent librement. Normaliser **à la
 * lecture** plutôt qu'à l'écriture rattrape aussi les enregistrements déjà en
 * magasin, sans passe de migration.
 */
export function normalizeMarkerType(type: string): string {
  return PREDEFINED_MARKER_TYPES.some((known) => known.id === type)
    ? type
    : MARKER_TYPE_OTHER
}

/** Symbole d'origine d'un type prédéfini, avant toute surcharge. */
export function defaultSymbolForType(typeId: string): string {
  return (
    PREDEFINED_MARKER_TYPES.find((type) => type.id === typeId)?.defaultSymbol ??
    '?'
  )
}

export interface CustomMarker {
  readonly type: string
  readonly name: string
  /** Deux caractères au maximum. */
  readonly symbol: string
  /** Point kilométrique, dans la convention marqueur (voir `markerDistanceOffset`). */
  readonly distanceKm: number
  readonly description: string
  readonly visible: boolean
  /**
   * Renseigné uniquement pour les marqueurs dérivés d'un way in/out.
   * Jamais sérialisé : ces marqueurs sont reconstruits depuis `wayInOuts`.
   */
  readonly wayId: string | null
}

export function createCustomMarker(
  init: Partial<CustomMarker> &
    Pick<CustomMarker, 'type' | 'name' | 'symbol' | 'distanceKm'>,
): CustomMarker {
  return {
    description: '',
    visible: true,
    wayId: null,
    ...init,
  }
}

/* -------------------------------------------------------------------------
 * Way in/out — itinéraires alternatifs
 * ---------------------------------------------------------------------- */

export interface WayVia {
  readonly lat: number
  readonly lon: number
}

export interface WayInOut {
  /**
   * Identifiant local. Régénéré à chaque import : les identifiants ne
   * survivent pas au format GPX, la correspondance se fait par définition
   * géométrique (voir `sameWayDefinition`).
   */
  readonly id: string
  readonly name: string
  /** Point de sortie, en km convention marqueur. */
  readonly exitKm: number
  /** Point de rentrée, en km convention marqueur. */
  readonly entryKm: number
  readonly vias: readonly WayVia[]
  readonly visible: boolean
}

/* -------------------------------------------------------------------------
 * Limites de robustesse du parseur
 * ---------------------------------------------------------------------- */

/**
 * Plafonds repris de `GpxParser.kt`. Un dépassement interrompt silencieusement
 * l'ajout : un fichier hostile ou simplement énorme est tronqué, jamais
 * remonté en erreur.
 */
export const LIMITS = {
  MAX_POINTS_PER_TRACK: 200_000,
  MAX_SEGMENTS_PER_TRACK: 100,
  MAX_TRACKS_PER_FILE: 50,
  MAX_WAYPOINTS_PER_FILE: 5_000,
  MAX_CUSTOM_MARKERS_PER_TRACK: 500,
  MAX_WAYS_PER_TRACK: 100,
  MAX_VIAS_PER_WAY: 25,
  MAX_WAY_ROUTE_CHARS: 200_000,
  /**
   * Budget de points de détour **partagé par tout le fichier**.
   *
   * Seule voie du parseur où la mémoire retenue dépasse largement la taille du
   * fichier. Les deux plafonds voisins ne se composaient pas :
   * `MAX_WAY_ROUTE_CHARS` est par détour et `MAX_WAYS_PER_TRACK` par trace, si
   * bien que 100 détours de 200 000 caractères font 10 millions de points
   * retenus pour une seule trace — cinquante fois le plafond de géométrie que
   * `MAX_POINTS_PER_TRACK` est censé imposer. La polyline est de l'ASCII et
   * deux caractères produisent un point : l'amplification est d'environ 24×,
   * et un GPX de 11 Mo suffit à épuiser la mémoire d'un onglet mobile, bien en
   * deçà des 50 Mo que l'import accepte.
   *
   * Un détour est un raccourci de quelques kilomètres, soit quelques centaines
   * de points : la marge reste très large. Épuisé, les géométries suivantes
   * sont ignorées et l'import recalcule par OSRM — le chemin déjà emprunté
   * pour une polyline absente ou corrompue.
   */
  MAX_WAY_ROUTE_POINTS_PER_FILE: 200_000,
  MAX_TEXT_LENGTH: 10_000,
  MAX_GPX_FILE_SIZE: 50 * 1024 * 1024,
} as const

/** Coordonnées valides — un point hors bornes est rejeté, pas corrigé. */
export function isValidLat(lat: number): boolean {
  return Number.isFinite(lat) && lat >= -90 && lat <= 90
}

export function isValidLon(lon: number): boolean {
  return Number.isFinite(lon) && lon >= -180 && lon <= 180
}
